/**
 * monthlyMedicationWordService.js
 *
 * Web version ของ generateMonthlyMedicationWord
 * ย้ายมาจาก Expo (expo-file-system / expo-sharing) → ใช้ Blob + <a> download แทน
 *
 * การใช้งานใน EditVN.jsx:
 *   import { generateMonthlyMedicationWord } from '../services/monthlyMedicationWordService';
 *   ...
 *   await generateMonthlyMedicationWord(patient, new Date());
 */

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * สร้างและดาวน์โหลดไฟล์ Word (.doc) ตารางบันทึกการให้ยารายเดือน
 *
 * @param {object} patient  — ข้อมูลผู้ป่วย (patient_name, room_number, ward_name, medications[])
 * @param {Date}   month    — เดือนที่ต้องการออกรายงาน (default: เดือนปัจจุบัน)
 */
export const generateMonthlyMedicationWord = (patient, month = new Date()) => {
  try {
    const monthStr    = formatThaiMonth(month);
    const daysInMonth = getDaysInMonth(month);

    const htmlContent = generateHTMLContent(patient, monthStr, daysInMonth);

    // สร้าง Blob — Word เปิด .doc ที่เป็น HTML ได้โดยตรง
    const blob = new Blob(['\ufeff', htmlContent], {
      type: 'application/msword;charset=utf-8',
    });

    const safeName = (patient.patient_name || 'patient').replace(/\s+/g, '_');
    const safeMonth = monthStr.replace(/\s+/g, '_');
    const filename  = `MedRecord_${safeName}_${safeMonth}.doc`;

    // ดาวน์โหลดผ่าน <a> tag
    const url = URL.createObjectURL(blob);
    const a   = document.createElement('a');
    a.href     = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log('✅ Monthly Medication Word downloaded:', filename);
  } catch (error) {
    console.error('❌ Error generating Monthly Medication Word:', error);
    throw new Error('ไม่สามารถสร้าง Word ได้: ' + error.message);
  }
};

// ---------------------------------------------------------------------------
// HTML generation (A4 Landscape, Word-compatible)
// ---------------------------------------------------------------------------

function generateHTMLContent(patient, monthStr, daysInMonth) {
  const rowHeight    = '35pt';
  const timeColWidth = '85pt';
  const medColWidth  = '180pt';

  return `
<html xmlns:o='urn:schemas-microsoft-com:office:office'
      xmlns:w='urn:schemas-microsoft-com:office:word'
      xmlns='http://www.w3.org/TR/REC-html40'>
<head>
  <meta charset="utf-8">
  <!--[if gte mso 9]><xml>
    <w:WordDocument>
      <w:View>Print</w:View>
      <w:Zoom>100</w:Zoom>
      <w:DoNotOptimizeForBrowser/>
    </w:WordDocument>
  </xml><![endif]-->
  <style>
    @page Section1 {
      size: 841.9pt 595.3pt; /* A4 Landscape */
      margin: 0.5in;
      mso-page-orientation: landscape;
    }
    div.Section1 { page: Section1; }

    * { box-sizing: border-box; }
    body { font-family: 'TH Sarabun New', 'Cordia New', 'Arial', sans-serif; margin: 0; padding: 0; }

    table { border-collapse: collapse; width: 100%; table-layout: fixed; }
    th, td {
      border: 0.5pt solid black;
      text-align: center;
      vertical-align: middle;
      height: ${rowHeight};
      font-family: 'TH Sarabun New', 'Cordia New', 'Arial', sans-serif;
      font-size: 12pt;
    }
    .col-time { width: ${timeColWidth}; background: #f2f2f2; font-weight: bold; }
    .col-med  { width: ${medColWidth}; text-align: left; font-size: 12pt; padding-left: 5pt; }

    /* ป้องกันแถวขาดกลางหน้าใน Word */
    tr { page-break-inside: avoid; }
  </style>
</head>
<body>
  <div class="Section1">
    <!-- Header -->
    <div style="text-align:center; margin-bottom:10pt;">
      <b style="font-size:16pt;">บันทึกการให้ยารายเดือน</b><br/>
      <span style="font-size:12pt;">
        <b>ชื่อ-สกุล:</b> ${patient.patient_name || '-'}
        &nbsp;&nbsp;
        <b>ห้อง:</b> ${patient.room_number || '-'}
        &nbsp;&nbsp;
        <b>วอร์ด:</b> ${patient.ward_name || '-'}
        &nbsp;&nbsp;
        <b>เดือน:</b> ${monthStr}
      </span>
    </div>

    <!-- Medication table -->
    <table>
      <thead>
        <tr style="background:#4A90E2; color:white;">
          <th style="width:${timeColWidth};">ช่วง</th>
          <th style="width:${medColWidth};">ชื่อยา</th>
          ${Array.from({ length: daysInMonth }, (_, i) =>
            `<th style="width:22pt; font-size:12pt;">${i + 1}</th>`
          ).join('')}
        </tr>
      </thead>
      <tbody>
        ${renderMedicationRows(patient, daysInMonth)}
      </tbody>

    </table>

    <!-- Signature section -->
    <div style="margin-top:20pt;">
      <table style="border:none; width:100%;">
        <tr style="border:none;">
          <td style="border:none; text-align:center; width:50%; height:60pt;">
            (......................................................)<br/>หัวหน้าพยาบาล
          </td>
          <td style="border:none; text-align:center; width:50%; height:60pt;">
            (......................................................)<br/>ผู้ตรวจสอบ
          </td>
        </tr>
      </table>
    </div>
  </div>
</body>
</html>`;
}

// ---------------------------------------------------------------------------
// Row rendering
// ---------------------------------------------------------------------------

const TIME_SLOTS = [
  { label: 'ก่อนอาหารเช้า',      timing: 'ac',  meal: 'เช้า'        },
  { label: 'หลังอาหารเช้า',       timing: 'pc',  meal: 'เช้า'        },
  { label: 'ก่อนอาหารกลางวัน',    timing: 'ac',  meal: 'กลางวัน'    },
  { label: 'หลังอาหารกลางวัน',    timing: 'pc',  meal: 'กลางวัน'    },
  { label: 'ก่อนอาหารเย็น',       timing: 'ac',  meal: 'เย็น'        },
  { label: 'หลังอาหารเย็น',       timing: 'pc',  meal: 'เย็น'        },
  { label: 'ก่อนนอน',             timing: 'hs',  meal: 'ก่อนนอน'    },
  { label: 'เมื่อมีอาการ',        timing: 'prn', meal: 'เมื่อมีอาการ' },
];

/**
 * ตรวจสอบว่า scheduleTime array มีมื้อที่ต้องการหรือไม่
 *
 * รองรับทั้ง 2 รูปแบบที่ API ส่งมา:
 *   1. ชื่อมื้อภาษาไทยโดยตรง  → ["เช้า", "กลางวัน", "เย็น", "ก่อนนอน", "เมื่อมีอาการ"]
 *   2. time string HH:MM       → ["08:00", "12:00", "18:00", "21:00"]
 */
function hasMealInSchedule(scheduleTime, targetMeal) {
  const TIME_TO_MEAL = {
    '07:30': 'เช้า', '08:00': 'เช้า', '08:30': 'เช้า',
    '11:30': 'กลางวัน', '12:00': 'กลางวัน', '12:30': 'กลางวัน',
    '17:30': 'เย็น', '18:00': 'เย็น', '18:30': 'เย็น',
    '21:00': 'ก่อนนอน',
  };

  return (scheduleTime || []).some(t => {
    const entry = (t || '').trim();
    // รูปแบบที่ 1: ชื่อมื้อภาษาไทยตรงๆ
    if (entry === targetMeal) return true;
    // รูปแบบที่ 2: HH:MM → แปลงก่อนเปรียบเทียบ
    return TIME_TO_MEAL[entry] === targetMeal;
  });
}

function renderMedicationRows(patient, daysInMonth) {
  return TIME_SLOTS.map(slot => {
    const meds = (patient.medications || []).filter(med => {
      const medTiming = (med.timing || '').toLowerCase();

      if (slot.timing === 'prn') {
        return medTiming === 'prn' || medTiming === 's.o.s' || medTiming === 'stat'
          || medTiming.includes('เมื่อมีอาการ');
      }

      if (slot.timing === 'hs') {
        return (medTiming === 'hs' || medTiming.includes('นอน'))
          && hasMealInSchedule(med.scheduleTime, 'ก่อนนอน');
      }

      // ac / pc — ต้องตรงทั้ง timing และ มื้อ
      const hasMeal = hasMealInSchedule(med.scheduleTime, slot.meal);

      let isCorrectTiming = false;
      if (slot.timing === 'ac') isCorrectTiming = medTiming === 'ac' || medTiming.includes('ก่อน');
      else if (slot.timing === 'pc') isCorrectTiming = medTiming === 'pc' || medTiming.includes('หลัง');

      return hasMeal && isCorrectTiming;
    });

    // แสดงอย่างน้อย 3 แถว หรือจำนวนยา + 2 แถวว่าง
    const totalRows = Math.max(3, meds.length + 2);
    const emptyCells = Array.from({ length: daysInMonth }, () => '<td></td>').join('');

    let html = '';
    for (let i = 0; i < totalRows; i++) {
      html += '<tr>';

      // Rowspan cell สำหรับแถวแรกของแต่ละช่วง
      if (i === 0) {
        html += `<td class="col-time" rowspan="${totalRows}">${slot.label}</td>`;
      }

      // ชื่อยา
      const med = meds[i];
      let medHTML = '';
      if (med) {
        const name        = med.medicationName || med.name || '';
        const dosage      = med.dosage              || '';
        const instruction = med.dosageInstruction   || med.dosage_instruction || '';
        const special     = med.specialInstruction  || '';

        medHTML = `<b>${name}</b> ${dosage}`;
        if (instruction) medHTML += `<br/>${instruction}`;
        if (special)     medHTML += `<br/><span style="color:red;">*${special}</span>`;
      }

      html += `<td class="col-med">${medHTML}</td>`;
      html += emptyCells;
      html += '</tr>';
    }

    // แถว ผู้จัดยา / ผู้สอบยา หลังจบแต่ละช่วงเวลา
    html += `
      <tr style="background:#f9f9f9;">
        <td class="col-time" style="font-size:11pt; font-weight:normal;">ผู้จัดยา</td>
        <td></td>
        ${emptyCells}
      </tr>
      <tr style="background:#f9f9f9;">
        <td class="col-time" style="font-size:11pt; font-weight:normal;">ผู้สอบยา</td>
        <td></td>
        ${emptyCells}
      </tr>`;

    return html;
  }).join('');
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function formatThaiMonth(date) {
  const months = [
    'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
    'ก.ค.', 'ส.ค.', 'ก.ย.',  'ต.ค.', 'พ.ย.', 'ธ.ค.',
  ];
  const d = new Date(date);
  return `${months[d.getMonth()]} ${d.getFullYear() + 543}`;
}

function getDaysInMonth(date) {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}